import { getString } from "common/ui_string_labels/ui-strings-utils";
import { useLocation, useNavigate } from "react-router-dom";
import { JSX, useEffect, useState } from "react";
import { AppLayout, AppLayoutProps, GridProps } from "@amzn/awsui-components-react";
import NavigationBreadcrumbs from "common/navigation/NavigationBreadcrumbs";
import Paths from "common/paths/paths";
import CustomGridLayout from "common/component/CustomGridLayout/CustomGridLayout";
import LocalITOrderConfirmationContent from "common/component/LocalITOrderConfirmationContent/LocalITOrderConfirmationContent";
import OrderInformationForm from "common/component/OrderInformationForm/OrderInformationForm";
import { getUseStandardOrder } from "hooks/useStandardOrder";
import { getUseNonStandardOrder } from "hooks/useNonstandardOrder";
import useCreateOrder, { getUseCreateOrder, useSubmitOrder } from "hooks/useCreateOrder";
import useFlashbar from "hooks/useFlashbar";
import { NavigateNewOrderResponse } from "hooks/useNewOrder";

const getStringPath: string = "views.itOrderConfirmation";



/**
 * Renders the LocalITOrderConfirmation component.
 * The components changes functionality between standard and non standard orders.
 *
 * @returns {JSX.Element} Placeholder text
 */
const LocalITOrderConfirmation = (): JSX.Element => {
    const location = useLocation();
    const navigate = useNavigate();
    const { itemsSelected, isOrderStandard, categories }: NavigateNewOrderResponse = location.state || {};

    /* APP LAYOUT CONFIGURATION */
    const contentType: AppLayoutProps.ContentType = "table";

    /* GRID LAYOUT CONFIGURATION */
    const grindLayout: ReadonlyArray<GridProps.ElementDefinition> = [
        { offset: { default: 0, xl: 1 }, colspan: { default: 6, l: 5 } },
        { offset: 1, colspan: { default: 5, l: 6, xl: 4 } },
    ];

    /* BREADCRUMBS */
    const breadcrumbs = {
        items: [
            { text: Paths.itOrderList.name, href: Paths.itOrderList.url },
            { text: Paths.itHardwareRestock.name, href: Paths.itHardwareRestock.url },
            { text: Paths.itOrderConfirmation.name, href: Paths.itOrderConfirmation.url },
        ],
    };

    /* Standard Order */
    const { standardOrder, setStandardOrder } = getUseStandardOrder(itemsSelected);

    /* Non Standard Order */
    const { nonStandardOrder, setNonStandardOrder } = getUseNonStandardOrder();

    /* Notification */
    const { Flashbar, generateMessage, generateError, removeItem } = useFlashbar(true);

    /* Form Values and Errors */
    const { formValues, setFormValues, formErrors, setFormErrors } = getUseCreateOrder();

    const { error: submitError, onSubmitOrder, order } = useSubmitOrder();

    /* Navigate to hardware restock */
    const goBackToHardwareRestockPage = () => {
        navigate(Paths.itHardwareRestock.url, {
            state: {
                isOrderStandard,
                standardOrder,
            },
        });
    };

    const {
        getHasError,
        customOnSubmitForm,
        isLoading: submitLoading,
    } = useCreateOrder({
        formValues,
        isOrderStandard,
        standardOrder,
        nonStandardOrder,
        formErrors,
        setFormErrors,
        onSubmitOrder,
        categories,
        generateError,
    });

    /* Notification Setters */

    const [loadingId, setLoadingId] = useState<string>();

    useEffect(() => {
        if (submitLoading) {
            const id = generateMessage(
                {
                    type: "success",
                    loading: true,
                    content: getString(`${getStringPath}.generatingOrder`),
                    dismissible: false,
                },
                true,
            );
            setLoadingId(id);
        } else {
            if (loadingId) removeItem(loadingId);
            setLoadingId(undefined);
        }
    }, [submitLoading]);

    useEffect(() => {
        generateError(submitError, true);
    }, [submitError]);

    return (
        <AppLayout
            navigationHide={true}
            toolsHide={true}
            notifications={Flashbar}
            breadcrumbs={<NavigationBreadcrumbs items={breadcrumbs.items} />}
            content={
                <CustomGridLayout
                    title={getString(`${getStringPath}.title`)}
                    leftSection={
                        <LocalITOrderConfirmationContent
                            getHasError={getHasError}
                            standardOrder={standardOrder}
                            setStandardOrder={setStandardOrder}
                            nonStandardOrder={nonStandardOrder}
                            setNonStandardOrder={setNonStandardOrder}
                            isOrderStandard={isOrderStandard}
                        />
                    }
                    rightSection={
                        <OrderInformationForm
                            getHasError={getHasError}
                            customOnSubmitForm={customOnSubmitForm}
                            formValues={formValues}
                            setFormValues={setFormValues}
                            loading={submitLoading}
                            goBackToHardwareRestockPage={goBackToHardwareRestockPage}
                        />
                    }
                    gridDefinition={grindLayout}
                />
            }
            contentType={contentType}
        />
    );
};

export default LocalITOrderConfirmation;
